import {
  CollapseSize,
  CollapseTitleSize,
} from '../../../components/collapsible/collapsible';
import {
  FavoriteDataResponse,
  FavoritesListResponse,
} from '../../graphql/queries/posts';
import {
  FilterType,
  IAccessoryFilter,
  IContentHubData,
  IFacetProps,
  IGridData,
  IMyAccountData,
  ISearchQuery,
  ISelectFilter,
  ISelectFilters,
  ITopFilters,
} from '../interfaces';
import {
  GetSiteConfigurationResponse,
  HeadlessConfig,
} from '../interfaces/sitte-configuration.interface';
import {
  addFavorite,
  deleteFavorite,
  fetchMyAccountInfo,
  fetchSearchData,
  fetchSiteConfiguration,
  getFavorites,
} from '../../services/dataService';
import {
  existsFavorite,
  refreshToken,
  validateToken,
} from '../../services/dataService';

import { IconType } from '../../../components/icon/Icon';
import { TooltipPlacement } from '../../../components/tooltip/tooltip';

export interface IActions {
  setInfoPanel(isVisible, title, richtText): void;
  clearInfoPanel: () => void;
  getSiteConfiguration: () => void;
  addToCounter: (amount: number) => void;
  removeFromCounter: (amount: number) => void;
  addAccessoryFilter: (filter: IAccessoryFilter, refeshData?: boolean) => void;
  removeAccessoryFilter: (filter: IAccessoryFilter) => void;
  clearAccessoryFilters: () => void;
  setContentHubData: (data: any) => void;
  getContentHubData: (filter: string, page: number, pageSize: number) => void;
  refreshUserToken: () => void;
  validateUserToken: (token: string) => void;
  getUserFavorites: () => Promise<FavoritesListResponse | undefined>;
  isFavorite: (id: string) => boolean;
  deleteUserFavorite: (id: string) => Promise<boolean>;
  addToUserFavorite: (id: string) => Promise<boolean>;
  bulkAddUserFavorite: (ids: string[]) => Promise<boolean>;
  bulkDeleteUserFavorite: (ids: string[]) => Promise<boolean>;
  existsUserFavorite: (id: string) => Promise<boolean>;
  getAccountInfoData: () => void;
  removeSelectedSystem: (id: number) => void;
  clearSelectedSystems: () => void;
  addToSelectedSystems: (system: any) => void;
  getSearchData: (search: ISearchQuery) => void;
  setSearchQuery: (query: string) => void;
  setSorting: (field: string, sort: '' | 'asc' | 'desc') => void;
  setSelectedTopFilterExclusive: (id: string) => void;
  setSelectedTopFilterNonExclusive: (id: string) => void;
  setFavoritesState: (enabled: boolean) => void;
  getLink: (item: any) => Promise<string | undefined>;
}

export interface IState {
  token: string | null | undefined;
  favorites: FavoriteDataResponse[] | undefined;
  hasToken: boolean;
  configuration: HeadlessConfig;
  selectedFacets: IAccessoryFilter[];
  selectedPageSize: number;
  gridData: IGridData[];
  counter: number;
  contentHubData: IContentHubData;
  myAccount?: IMyAccountData;
  selectedSystems: any[];
  query: ISearchQuery;
  results: IContentHubData;
  selectFilters: ISelectFilters;
  topFilterListExclusive: ITopFilters[];
  topFilterListNonExclusive: ITopFilters[];
  isLoading: boolean;
  infoPanel?: {
    isVisible: boolean;
    title: string;
    richtText: string[];
  };
}

export interface IStore {
  state: IState;
  setState: (state: IState) => void;
}

export const addToCounter = (store: IStore, amount: number) => {
  const counter = store.state.counter + amount;
  store.setState({ ...store.state, counter });
};

export const removeFromCounter = (store: IStore, amount: number) => {
  const counter = store.state.counter - amount;
  store.setState({ ...store.state, counter });
};

export const setSelectedTopFilterExclusive = (store: IStore, id: string) => {
  let selectFilters = {
    ...store.state.selectFilters,
  } as ISelectFilters;

  if (id === 'favorites') {
    selectFilters.favoritesTopFilterEnabled =
      !selectFilters.favoritesTopFilterEnabled;
  } else {
    if (store.state.selectFilters.selectedExclusiveTopFilter === id) {
      selectFilters.selectedExclusiveTopFilter = '';
    } else {
      selectFilters.selectedExclusiveTopFilter = id;
    }
  }

  const query = {
    ...store.state.query,
    selectFilters,
  };

  store.setState({ ...store.state, selectFilters, query });

  actions.getSearchData(store, query);
};

export const setSelectedTopFilterNonExclusive = (store: IStore, id: string) => {
  const selectFilters = { ...store.state.selectFilters } as ISelectFilters;
  if (!selectFilters.selectedNonExclusiveTopFilter.includes(id)) {
    selectFilters.selectedNonExclusiveTopFilter.push(id);
  } else {
    selectFilters.selectedNonExclusiveTopFilter =
      selectFilters.selectedNonExclusiveTopFilter.filter(
        (filterId) => filterId !== id
      );
  }
  const query = store.state.query;
  query.selectFilters = selectFilters;
  store.setState({ ...store.state, selectFilters, query });
  actions.getSearchData(store, {
    ...store.state.query,
  });
};

const setSorting = (
  store: IStore,
  field: string,
  sort: '' | 'asc' | 'desc'
) => {
  const query = store.state.query;
  query.sort = {
    sortName: field,
    ascending: sort === 'asc',
  };
  store.setState({ ...store.state, query });
  actions.getSearchData(store, query);
};

export const createAccessoryFilterfromSelectFilter = (
  state: IState,
  filter: ISelectFilter
): IAccessoryFilter | undefined => {
  const item = state.contentHubData.facets.find(
    (f: IFacetProps) => f.name === filter.fieldName
  );
  if (item) {
    const accessoryFilter = item.values.find(
      (af: IAccessoryFilter) => af.key === filter.value
    );
    return accessoryFilter;
  }
  return undefined;
};

export const createAccessoryFilterFromFacetProps = (
  filter: IAccessoryFilter,
  props: IFacetProps[]
): IAccessoryFilter => {
  const prop = props.filter((p) => p.name === filter.groupId);
  if (prop.length > 0) {
    return {
      ...filter,
      filterType: prop[0].filterType,
      groupId: prop[0].name,
      includedInTopFilter: prop[0].hideThisValues || false,
      hideThisValue: prop[0].hideThisValues || false,
    };
  }
  return filter;
};

export const createSelectFilterfromAccessoryFilter = (
  accessoryFilter: IAccessoryFilter
): any => {
  return {
    value: accessoryFilter.value,
    fieldName: accessoryFilter.groupId || '',
    filterType: accessoryFilter.filterType,
    includedInTopFilter: accessoryFilter.includedInTopFilter,
  };
};

export const addAccessoryFilter = (
  store: IStore,
  filter: IAccessoryFilter,
  refreshData: boolean = false
) => {
  let selectedFacets = store.state.selectedFacets;

  // Handle radio group logic
  if (filter.filterType === FilterType.RADIO && filter.groupId) {
    // Remove any existing filters from the same group
    selectedFacets = selectedFacets.filter((f) => f.groupId !== filter.groupId);
  }

  if (filter.filterType === FilterType.SEARCH) {
    selectedFacets = selectedFacets.filter(
      (f) => f.filterType !== FilterType.SEARCH
    );
  }

  const existingFilterIndex = selectedFacets.findIndex(
    (f) => f.id === filter.id
  );

  if (existingFilterIndex !== -1 && refreshData) {
    if (filter.filterType === FilterType.SLIDER) {
      selectedFacets[existingFilterIndex] = {
        ...selectedFacets[existingFilterIndex],
        value: filter.value,
        key: filter.value,
      };
    }
    // otherwise already removed by group filter above
  } else {
    selectedFacets.push(filter);
  }

  store.setState({ ...store.state, selectedFacets });

  if (refreshData) {
    actions.getSearchData(store, {
      ...store.state.query,
      selectFilters: {
        ...store.state.selectFilters,
        filters: selectedFacets.map((f) => ({
          value: f.value,
          fieldName: f.groupId || '',
          filterType: f.filterType,
          includedInTopFilter: f.includedInTopFilter || false,
        })),
      },
    });
  }
};

export const removeAccessoryFilter = (
  store: IStore,
  filter: IAccessoryFilter
) => {
  let selectedFacets = store.state.selectedFacets;
  let selectedTextQueries =
    store.state.contentHubData.selectFilters.selectedTextQueries;

  if (filter.filterType === FilterType.RADIO && filter.groupId) {
    // Remove all filters in the same group
    selectedFacets = selectedFacets.filter((f) => f.groupId !== filter.groupId);
  } else {
    // Remove just the specific filter
    selectedFacets = selectedFacets.filter((f) => f.id !== filter.id);
  }

  if (filter.filterType === FilterType.SEARCH) {
    selectedTextQueries = selectedTextQueries.filter(
      (stq) => `text-query-${stq}` !== filter.id
    );
  }

  store.setState({ ...store.state, selectedFacets });

  const selectFacets = selectedFacets.map((f) =>
    createSelectFilterfromAccessoryFilter(f)
  );

  actions.setSearchQuery(store, '');

  store.setState({
    ...store.state,
    selectFilters: {
      ...store.state.selectFilters,
      filters: selectFacets,
      selectedTextQueries,
    },
  });

  actions.getSearchData(store, {
    ...store.state.query,
    selectFilters: {
      ...store.state.selectFilters,
      filters: selectFacets,
      selectedTextQueries,
    },
    query: '', // Reset query to empty string when removing a filter
  });
};

export const setSearchQuery = (store: IStore, query: string) => {
  store.setState({ ...store.state, query: { ...store.state.query, query } });
};

export const getSiteConfiguration = (store: IStore) => {
  fetchSiteConfiguration().then((response) => {
    if (response && response.optimizely) {
      var {
        optimizely: { getHeadlessConfig },
      } = response as GetSiteConfigurationResponse;
      if (!getHeadlessConfig) {
        console.error('getHeadlessConfig is undefined');
        return;
      }
      console.log('Site configuration response:', getHeadlessConfig);
      var facets =
        getHeadlessConfig.smartSystemSelectorContent[0].facetMapConfig
          ?.facetMapSection?.facetConfigurations || [];
      store.setState({
        ...store.state,
        configuration: { ...getHeadlessConfig, facets },
      });
    }
  });
};

export const isHiddenFacet = (
  facet: IFacetProps,
  selectedFields: ISelectFilter[]
): boolean => {
  // Check if the facet is included in the top filter and has only one value
  const includedInTopFilter = selectedFields.some(
    (f: ISelectFilter) =>
      (f.fieldName === facet.name || f.fieldName === facet.displayName) &&
      f.includedInTopFilter === true
  );
  if (includedInTopFilter && facet.values.length === 1) {
    return true; // If the facet is included in the top filter, it should be hidden
  }
  return false;
};

export const getFacetContent = (
  facet: IFacetProps,
  selectedFields: ISelectFilter[],
  type: FilterType
): IAccessoryFilter[] => {
  return facet.values.map((item: IAccessoryFilter) => {
    const includedInTopFilter = selectedFields.some(
      (f: ISelectFilter) =>
        f.fieldName === facet.name && f.includedInTopFilter === true
    );
    // console.log('INCLUDED FACET', facet);
    // console.log('INCLUDED FIELDS', selectedFields);
    // console.log('AMOUNT', selectedFields.length);
    // console.log('CANHIDE', includedInTopFilter);

    let id = `${facet.name}-${item.key}-${item.displayName}`;
    switch (type) {
      case FilterType.SLIDER:
        id = `${type}-${facet.name}`;
        break;
      default:
    }
    return {
      key: item.key,
      id: id,
      hideThisValue: item.hideThisValue || includedInTopFilter,
      label: `${item.displayName}`,
      value: item.key,
      groupId: facet.name,
      iconImage: item.iconImage || '',
      filterType: type,
      includedInTopFilter,
      countText: item.countText || '',
    };
  });
};

export const getFilterType = (filterType: string): FilterType => {
  switch (filterType) {
    case 'MULTI_SELECT_OR':
      return FilterType.CHECKBOX;
    case 'MULTI_SELECT_AND':
    case 'SINGLE_SELECT':
      return FilterType.RADIO;
    case 'MINIMUM':
      return FilterType.SLIDER;
    default:
      console.warn(`Unknown filter type: ${filterType}`);
      return FilterType.CHECKBOX; // Default to checkbox if unknown
  }
};

export const isFavorite = (store: IStore, id: string) => {
  const found = store.state.favorites?.find((item) => item.entityId === id);
  return !!found;
};

export const getSearchData = (store: IStore, search: ISearchQuery) => {
  store.setState({ ...store.state, isLoading: true });

  fetchSearchData(search)
    .then((response) => {
      if (response && response.search) {
        let initial: IContentHubData = {
          favoriteIds: response.search.favoriteIds,
          isAuthenticated: response.search.isAuthenticated,
          count: 24,
          currentPage: 1,
          pageCount: 143,
          pageSize: 24,
          isLastPage: false,
          totalCount: 3425,
          listerFields: [],
          gridFeaturedFields: [],
          gridPerformanceFields: [],
          quickViewFields: [],
          attributesForSortingDropdown: [],
          list: [],
          facets: [],
          selectFilters: {
            filters: [],
            selectedExclusiveTopFilter: '',
            selectedNonExclusiveTopFilter: [],
            selectedTextQueries: [],
            favoritesTopFilterEnabled: false,
          },
          topFilterListExclusive: [],
          topFilterListNonExclusive: [],
          sortFilter: {
            sortName: '',
            ascending: true,
          },
        };

        let contentHubData = {
          ...initial,
          ...response.search,
        } as IContentHubData;

        let listerFields = contentHubData.listerFields.map(
          (field: any, index: number) => {
            const listerField = {
              field: field,
              label: field,
              isLeftSticky: index === 0 ? true : false,
              sort: '',
            };
            if ((contentHubData as any).sortBy?.fieldName === field) {
              listerField.sort = (contentHubData as any).sortBy.sortByAscending
                ? 'asc'
                : 'desc';
            }
            return listerField;
          }
        );

        const selectedFields = contentHubData.selectFilters.filters;
        let facets = contentHubData.facets.map((facet, index) => {
          let facetConfig = store.state.configuration.facets?.find(
            (f: any) => f.attributeConfig.fieldName === facet.name
          );
          if (!facetConfig) {
            console.warn(`Facet configuration not found for ${facet.name}`);
            facetConfig = undefined;
          }
          const filterType = getFilterType(
            (facetConfig as any)?.behavior || 'MULTI_SELECT_OR'
          );
          const facetView = {
            id: facet.id || Math.random(),
            name: facet.name,
            description: facet.description || '',
            displayName:
              (facetConfig as any)?.attributeConfig.label || facet.name,
            hideThisValues: isHiddenFacet(facet, selectedFields),
            values: getFacetContent(facet, selectedFields, filterType),
            collapse: {
              open: false,
              closeOn: 'click',
              size: CollapseSize.MEDIUM,
              title: (facetConfig as any)?.label ?? facet.name,
              titleSize: CollapseTitleSize.MEDIUM,
              tooltip: {
                content: facetConfig?.tagline || '',
                placement: TooltipPlacement.TOP,
                icon: {
                  iconType: IconType.Info,
                  className: 'u-icon-small',
                },
              },
              numberOfLines: 3,
            },
            filterType: filterType,
            content: getFacetContent(facet, selectedFields, filterType),
            props: facetConfig,
          };
          return facetView;
        });

        let list = contentHubData.list.map((item) => {
          const properties = item.properties as any[];
          //const nameValue = properties.find((p) => p.key === 'name')?.value;

          return {
            ...item,
            extraClass: 'm-card--sys',
            image: item.images?.[0] || '/assets/images/cards/system.jpeg',
            badges: item.badges || [
              { label: 'Default label', type: 'default' },
            ],
            title: properties.find((p) => p.key === 'name')?.value,
            name: properties.find((p) => p.key === 'name')?.value,
            id: item.id || Math.random(),
            // select item from array
            systemCode:
              item.systemCode ||
              properties.find((p) => p.key === 'name')?.value,
            specs: item.specs || [
              { label: 'Duty rating', value: 'Severe' },
              { label: 'Nominal thickness', value: '144 mm' },
              { label: 'Classification', value: 'EN' },
            ],
            link: getLink(item),
            hasBackground: true,
            favoriteCTA: true,
            viewCTA: 'Quick view',
          };
        });

        const selectFilters = contentHubData.selectFilters;
        const topFilterListExclusive =
          contentHubData.topFilterListExclusive.map((filter) => {
            if (
              filter.filterDisplayLabel ===
              selectFilters.selectedExclusiveTopFilter
            ) {
              return { ...filter, active: true };
            } else {
              return { ...filter, active: false };
            }
          });

        const topFilterListNonExclusive =
          contentHubData.topFilterListNonExclusive.map((filter) => {
            if (
              selectFilters.selectedNonExclusiveTopFilter.includes(
                filter.filterDisplayLabel
              )
            ) {
              return { ...filter, active: true };
            } else {
              return { ...filter, active: false };
            }
          });

        store.state.selectedFacets.forEach((filter: IAccessoryFilter) => {
          const foundFilter = selectFilters.filters.find(
            (f) => f.fieldName === filter.groupId && f.value === filter.value
          );
          if (!foundFilter) {
            // remove filter from selectedFacets if not found in selectFilters
            console.warn(
              `Filter ${filter.groupId} with value ${filter.value} not found in selectFilters`
            );
            store.state.selectedFacets = store.state.selectedFacets.filter(
              (f) => f.id !== filter.id
            );
          }
        });

        store.setState({
          ...store.state,
          favorites: contentHubData.favoriteIds.map((id) => ({
            entityId: id,
            entityTypeKey: 'system',
            userId: '',
          })),
          contentHubData: {
            ...contentHubData,
            list,
            listerFields,
            facets: facets as any,
            topFilterListExclusive,
            topFilterListNonExclusive,
          },
          selectedPageSize: 24,
          isLoading: false,
        });
        console.log('STATE', store.state);
        console.log('CONTENTHUBDATA', store.state.contentHubData);
      } else {
        store.setState({ ...store.state, isLoading: false });
      }

      // Refresh user token after fetching search data
      refreshUserToken(store);
    })
    .catch((error) => {
      console.error('Error fetching data:', error);
      store.setState({ ...store.state, isLoading: false });
    });
};

export const setContentHubData = (store: IStore, contentHubData: any) => {
  store.setState({ ...store.state, contentHubData });
};

export const refreshUserToken = (store: IStore) => {
  if (!store.state.token) {
    const token = sessionStorage.getItem('accessToken');
    store.setState({ ...store.state, token });
  }
  refreshToken().then((response) => {
    if (response && response.authentication) {
      const token = response.authentication?.refreshToken as string;
      window.sessionStorage.setItem('accessToken', token);
      store.setState({ ...store.state, token, hasToken: true });
      console.log('Token refreshed successfully.');
    } else {
      console.warn('No authentication data in response');
      store.setState({ ...store.state, token: undefined, hasToken: false });
    }
  });
};

export const validateUserToken = (store: IStore, token: string) => {
  validateToken(token).then((response) => {
    if (response && response.authentication) {
      const responseString = response.authentication?.validateToken as string;
      const isValid = responseString === 'Token is valid';
      if (!isValid) {
        console.warn('Token is not valid');
        store.setState({ ...store.state, token: undefined });
        window.sessionStorage.removeItem('accessToken');
      }
    }
  });
};

export const getUserFavorites = async (store: IStore) => {
  getFavorites().then((response) => {
    if (response && response.favorites) {
      const favorites = response.favorites.list || [];
      store.setState({ ...store.state, favorites });
    } else {
      console.warn('No favorites found in response');
      store.setState({ ...store.state, favorites: [] });
    }
  });
};

export const deleteUserFavorite = async (store: IStore, id: string) => {
  return deleteFavorite(id).then((response) => {
    if (response) {
      store.setState({
        ...store.state,
        favorites: store.state.favorites?.filter((fav) => fav.entityId !== id),
      });
    }
  });
};

export const addToUserFavorite = async (store: IStore, id: string) => {
  addFavorite(id).then((response) => {
    if (response) {
      getUserFavorites(store);
    }
  });
};

export const bulkAddUserFavorite = async (store: IStore, ids: string[]) => {
  return addFavorite(ids.join(',')).then((response) => {
    if (response) {
      getUserFavorites(store);
    }
  });
};

export const bulkDeleteUserFavorite = async (store: IStore, ids: string[]) => {
  return deleteFavorite(ids.join(',')).then((response) => {
    if (response) {
      getUserFavorites(store);
    }
  });
};

export const existsUserFavorite = async (id: string): Promise<boolean> => {
  return existsFavorite(id).then((response) => {
    return response;
  });
};

export const getAccountInfoData = (store: IStore) => {
  fetchMyAccountInfo().then((response) => {
    if (response && response.authentication) {
      const myAccount = response.authentication?.myAccount as IMyAccountData;
      store.setState({ ...store.state, myAccount });
    }
  });
};

export const clearAccessoryFilters = (store: IStore) => {
  store.setState({ ...store.state, selectedFacets: [] });
  actions.setSearchQuery(store, '');
  actions.getSearchData(store, {
    ...store.state.query,
    query: '',
    selectFilters: {
      ...store.state.selectFilters,
      filters: [],
      selectedExclusiveTopFilter: '',
      selectedNonExclusiveTopFilter: [],
      selectedTextQueries: [],
      favoritesTopFilterEnabled: false,
    },
  });
};

export const removeSelectedSystem = (store: IStore, id: number) => {
  const selectedSystems = store.state.selectedSystems.filter(
    (system) => system.id !== id
  );
  store.setState({ ...store.state, selectedSystems });
};

export const clearSelectedSystems = (store: IStore) => {
  store.setState({ ...store.state, selectedSystems: [] });
};

export const addToSelectedSystems = (store: IStore, system: any) => {
  const selectedSystems = store.state.selectedSystems;
  if (!selectedSystems.some((s) => s.id === system.id)) {
    selectedSystems.push(system);
  }
  store.setState({ ...store.state, selectedSystems });
};

export const setInfoPanel = (
  store: IStore,
  isVisible: boolean,
  title: string,
  richtText: string[]
) => {
  store.setState({
    ...store.state,
    infoPanel: {
      isVisible,
      title,
      richtText,
    },
  });
};

export const clearInfoPanel = (store: IStore) => {
  store.setState({
    ...store.state,
    infoPanel: {
      isVisible: false,
      title: '',
      richtText: [],
    },
  });
};

export const setFavoritesState = (store: IStore, enabled: boolean) => {
  store.setState({
    ...store.state,
    contentHubData: {
      ...store.state.contentHubData,
      selectFilters: {
        ...store.state.contentHubData?.selectFilters,
        favoritesTopFilterEnabled: enabled,
      },
    },
  });
};

export const getLink = (item: any) => {
  const urlSlug =
    item.id || item.name?.toLowerCase().replace(/\s+/g, '-') || '';

  return (
    item.link ||
    `/en-gb/products-and-systems/systems/partition-systems/${urlSlug}`
  );
};

const actions = {
  getSiteConfiguration,
  addToCounter,
  removeFromCounter,
  addAccessoryFilter,
  removeAccessoryFilter,
  clearAccessoryFilters,
  setSearchQuery,
  setContentHubData,
  // getContentHubData,
  refreshUserToken,
  validateUserToken,
  getUserFavorites,
  isFavorite,
  deleteUserFavorite,
  addToUserFavorite,
  bulkAddUserFavorite,
  bulkDeleteUserFavorite,
  // existsUserFavorite,
  getAccountInfoData,
  removeSelectedSystem,
  clearSelectedSystems,
  addToSelectedSystems,
  getSearchData,
  setSorting,
  setSelectedTopFilterExclusive,
  setSelectedTopFilterNonExclusive,
  setInfoPanel,
  clearInfoPanel,
  setFavoritesState,
  getLink,
};
export default actions;
